
import { defineComponent } from "vue";
import { DatasetRecord, GDT } from "@/types";
import Spinner from "@/components/shared/Spinner.vue";
import { BvTableFieldArray } from "bootstrap-vue";
import {
  useDataSource,
  useGammaData,
  useLoading,
  useLocation,
} from "@/services";
import { mapState } from "pinia";
import { DisplayColumn } from "@gamma/types.data";

export default defineComponent({
  name: "DataViewer",
  components: {
    Spinner,
  },
  props: {
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["updateSelection"],
  data: () => ({
    selectedRows: [] as any[],
    selection: [] as GDT.DisplayColumn[],
  }),
  computed: {
    ...mapState(useGammaData, [
      "tableDefinition",
      "datasetDefinition",
      "activeSourceId",
      "columnDefinitions",
      "request",
      "displayColumns",
    ]),
    isLoadingData(): boolean {
      return useLoading().isLoadingData("table");
    },
    selectColumns(): any[] {
      return this.selection.map((c) => ({
        text: c.label,
        value: c.name,
        visible: c.visible,
      }));
    },
    columns(): DisplayColumn[] {
      const sourceStore = useDataSource(this.activeSourceId)();

      const displayColumnsMap = this.displayColumns.reduce(
        (x, y) => x.set(y.name, y),
        new Map<string, DisplayColumn>()
      );

      return sourceStore.bottomColumnDefinitions.map(
        (x) =>
          ({
            ...displayColumnsMap.get(x.columnName),
            label: x.displayName,
            visible: x.visible,
          } as DisplayColumn)
      );
    },
    fields(): BvTableFieldArray {
      return (
        (this.selectable
          ? [{ key: "selected", label: "" }]
          : []) as BvTableFieldArray
      ).concat(
        this.columns
          .filter((c) => c.visible)
          .map((c) => ({
            key: c.name,
            label: c.label,
            sortable: this.activeSourceId
              ? useDataSource(this.activeSourceId)().columnDefinitionsMap.get(
                  c.name
                )?.sortable
              : false,
          }))
      );
    },
    rows(): Record<string, unknown>[] {
      return this.activeSourceId
        ? useDataSource(this.activeSourceId)().rows
        : [];
    },
    orderBys(): GDT.OrderBy[] {
      const ds = this.activeSourceId
        ? useDataSource(this.activeSourceId)()
        : null;
      return ds ? (ds.orderBys.length ? ds.orderBys : ds.defaultOrderBys) : [];
    },
    sortBy(): string | undefined {
      return this.orderBys[0]?.columnName;
    },
    isDesc(): boolean | undefined {
      return this.orderBys[0]?.keyword === GDT.OrderKeyword.Desc;
    },
    checkAll: {
      get(): boolean {
        return this.selectedRows.length === this.rows.length;
      },
      set(val: boolean) {
        if (!val) {
          (this.$refs.Table as any).selectAllRows();
        } else {
          (this.$refs.Table as any).clearSelected();
        }
      },
    },
  },
  methods: {
    getGroupNumber(item: Record<string, any>): number {
      return typeof item["GROUP_NUMBER"] === "number"
        ? item["GROUP_NUMBER"]
        : 0;
    },
    sortChanged(ctx: any) {
      if (this.activeSourceId) {
        useDataSource(this.activeSourceId)().update("orderBys", [
          {
            columnName: ctx.sortBy,
            keyword: ctx.sortDesc
              ? GDT.OrderKeyword.Desc
              : GDT.OrderKeyword.Asc,
          },
        ]);
      }
    },
    toggleColumn(col: any) {
      const index = this.selection.findIndex((c) => c.name === col.value);
      if (index > -1) {
        this.selection.splice(index, 1, {
          ...this.selection[index],
          visible: !this.selection[index].visible,
        });
      }
    },
    onRowSelected(data: Record<string, string>[]) {
      if (this.selectable) {
        this.selectedRows = data;
      } else {
        useLocation().updateLocation({
          query: data[0],
          lcId: this.activeSourceId,
          type: "Record",
        });
      }
    },
    updateSelection(data: Record<string, string>[]) {
      if (this.activeSourceId) {
        this.$emit(
          "updateSelection",
          data.map(
            (el) =>
              new DatasetRecord(
                [],
                useDataSource(this.activeSourceId)().columnDefinitions,
                {
                  ...el,
                  lcId: this.activeSourceId,
                }
              )
          )
        );
      }
    },
  },
  watch: {
    rows() {
      this.selectedRows = [];
    },
    selectedRows: "updateSelection",
  },
});
